/**
 * CRM 고객 연락처·생년월일 입력 표시용 (저장값은 숫자만 또는 YYYY-MM-DD).
 */

/**
 * 엑셀·CSV에서 맨 앞 0이 빠진 국내 전화번호(숫자만)를 보정한다.
 * 이미 0으로 시작하거나 길이가 맞으면 그대로 둔다.
 * @param {string} digits 숫자만
 * @returns {string}
 */
export function restoreLeadingZeroKoreanPhoneDigits(digits) {
  const d = String(digits || '').replace(/\D/g, '')
  if (!d) return ''
  if (d.startsWith('0')) return d

  // 10자리: 앞 0 하나만 누락된 흔한 패턴 (010·011·016~019, 02, 031~064, 070 등)
  if (d.length === 10) {
    if (/^(10|11|16|17|18|19)[0-9]{8}$/.test(d)) return `0${d}`
    if (/^2[0-9]{8}$/.test(d)) return `0${d}`
    if (/^3[1-6][0-9]{8}$/.test(d)) return `0${d}`
    if (/^4[1-4][0-9]{8}$/.test(d)) return `0${d}`
    if (/^5[1-5][0-9]{8}$/.test(d)) return `0${d}`
    if (/^6[1-4][0-9]{8}$/.test(d)) return `0${d}`
    if (/^70[0-9]{8}$/.test(d)) return `0${d}`
  }
  // 8~9자리: 서울 02 등 (앞 0만 빠진 경우)
  if ((d.length === 8 || d.length === 9) && /^2[0-9]{7,8}$/.test(d)) return `0${d}`

  return d
}

/** @param {string} value */
export function formatKoreanPhoneInput(value) {
  const x = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 11)
  if (!x) return ''
  if (x.startsWith('02')) {
    if (x.length <= 2) return x
    if (x.length >= 10) {
      const ten = x.length >= 11 ? x.slice(0, 10) : x
      return `${ten.slice(0, 2)}-${ten.slice(2, 6)}-${ten.slice(6)}`
    }
    if (x.length <= 5) return `${x.slice(0, 2)}-${x.slice(2)}`
    return `${x.slice(0, 2)}-${x.slice(2, 5)}-${x.slice(5)}`
  }
  if (x.length <= 3) return x
  if (x.length <= 7) return `${x.slice(0, 3)}-${x.slice(3)}`
  if (x.length === 10) return `${x.slice(0, 3)}-${x.slice(3, 6)}-${x.slice(6)}`
  return `${x.slice(0, 3)}-${x.slice(3, 7)}-${x.slice(7)}`
}

/** @param {string} value */
export function formatBirthYmdInput(value) {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 8)
  if (digits.length <= 4) return digits
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`
}

/**
 * 생년월일 필드 → API `birthDate` (YYYY-MM-DD) 또는 null.
 * @returns {{ ok: true, value: string|null } | { ok: false, message: string }}
 */
export function normalizeBirthDateForApi(display) {
  const trimmed = String(display || '').trim()
  if (!trimmed) return { ok: true, value: null }
  const d = trimmed.replace(/\D/g, '')
  if (d.length !== 8) {
    return { ok: false, message: '생년월일은 숫자 8자리(YYYYMMDD)까지 입력해 주세요.' }
  }
  const y = Number(d.slice(0, 4))
  const m = Number(d.slice(4, 6))
  const day = Number(d.slice(6, 8))
  if (m < 1 || m > 12 || day < 1 || day > 31) {
    return { ok: false, message: '생년월일 날짜를 확인해 주세요.' }
  }
  const dt = new Date(y, m - 1, day)
  if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day) {
    return { ok: false, message: '생년월일 날짜를 확인해 주세요.' }
  }
  return { ok: true, value: `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` }
}

/** 엑셀 날짜 직렬값(1900-01-01 기준 일수) → YYYY-MM-DD (UTC 자정 기준) */
function excelSerialToYmdUtc(serial) {
  const n = Math.floor(Number(serial))
  if (!Number.isFinite(n) || n < 20000 || n > 65000) return null
  const epoch = Date.UTC(1899, 11, 30)
  const d = new Date(epoch + n * 86400000)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getUTCFullYear()
  if (y < 1930 || y > 2100) return null
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/**
 * CSV 가져오기용: 일반 입력 + 엑셀에서 생년월일이 숫자(직렬값)만 있는 경우.
 * @returns {{ ok: true, value: string|null } | { ok: false, message: string }}
 */
export function normalizeBirthDateForCsvImport(display) {
  const primary = normalizeBirthDateForApi(display)
  if (primary.ok) return primary
  const trimmed = String(display ?? '').trim()
  if (!trimmed) return { ok: true, value: null }
  const n = Math.round(Number(trimmed.replace(/[, ]/g, '')))
  if (!Number.isFinite(n)) return primary
  if (n >= 20000 && n <= 65000) {
    const ymd = excelSerialToYmdUtc(n)
    if (ymd) {
      return normalizeBirthDateForApi(ymd)
    }
  }
  return primary
}
